import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Plus, Image as ImageIcon, Send, X, User } from 'lucide-react';
import { createPost, Post } from '@/db/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface PostFormProps {
  onSuccess?: (post: Post) => void;
}

const PostForm: React.FC<PostFormProps> = ({ onSuccess }) => {
  const { profile } = useAuth();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) {
      toast.error('请先登录后发帖');
      return;
    }

    if (!title.trim() || !content.trim()) {
      toast.error('标题和内容不能为空');
      return;
    }

    setIsSubmitting(true);
    try {
      const newPost = await createPost({
        user_id: profile.id,
        title: title.trim(),
        content: content.trim(),
        images: images,
        tags: []
      });
      toast.success('发布成功');
      setTitle('');
      setContent('');
      setImages([]);
      onSuccess?.(newPost);
    } catch (err) {
      toast.error('发布失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="rounded-xl border-primary/20 bg-primary/5 mb-8 shadow-neon overflow-hidden">
      <CardHeader className="p-4 pb-2 border-b border-primary/10 flex flex-row items-center gap-2">
        <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
          <Plus className="h-5 w-5" />
          发布动态
        </CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="p-4 pt-4 space-y-4">
          <Input 
            placeholder="标题" 
            className="rounded-lg border-primary/20 bg-background/50 h-10 font-bold"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Textarea 
            placeholder="分享你的灵感或经验..." 
            className="rounded-lg border-primary/20 bg-background/50 min-h-[120px] resize-none"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          
          {images.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {images.map((img, i) => (
                <div key={i} className="w-16 h-16 rounded-lg relative overflow-hidden bg-muted group">
                  <img src={img} alt="preview" className="w-full h-full object-cover" />
                  <button 
                    className="absolute top-1 right-1 p-0.5 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="p-4 pt-2 border-t border-primary/10 bg-primary/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 text-primary hover:bg-primary/10"
              onClick={() => {
                // Mock image upload
                setImages(prev => [...prev, 'https://picsum.photos/400/300?random=' + Math.random()]);
              }}
            >
              <ImageIcon className="h-5 w-5" />
            </Button>
          </div>
          <Button 
            disabled={isSubmitting}
            type="submit" 
            className="bg-primary text-white hover:bg-primary/90 rounded-lg h-9 shadow-md flex items-center gap-2 font-bold px-6"
          >
            {isSubmitting ? '发布中...' : '发布'}
            <Send className="h-4 w-4" />
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
};

export default PostForm;
